"use client";

import type { JSX } from "react";
import dynamic from "next/dynamic";
import { useState } from "react";
import { trackCalculatorUse } from "@/lib/analytics";

const MacroDoughnutChart = dynamic(() => import("../components/MacroDoughnutChart"), {
  ssr: false,
  loading: () => (
    <div
      className="h-64 w-64 animate-pulse rounded-full bg-gray-100"
      aria-label="Loading macro chart"
      role="status"
    />
  ),
});

interface MacroResults {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
}

interface ValidationErrors {
  age?: string;
  height?: string;
  weight?: string;
}

export default function CalculatorClient(): JSX.Element {
  const [isMetric, setIsMetric] = useState(true);
  const [formData, setFormData] = useState({
    age: "",
    gender: "male",
    height: "",
    weight: "",
    goal: "maintenance",
    activityLevel: "sedentary",
  });
  const [results, setResults] = useState<MacroResults | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Age validation
    if (!formData.age) {
      newErrors.age = "Age is required";
    } else {
      const age = Number(formData.age);
      if (age < 15 || age > 100) {
        newErrors.age = "Age must be between 15 and 100";
      }
    }

    // Height validation
    if (!formData.height) {
      newErrors.height = "Height is required";
    } else {
      const height = Number(formData.height);
      if (isMetric) {
        if (height < 130 || height > 230) {
          newErrors.height = "Height must be between 130cm and 230cm";
        }
      } else {
        if (height < 50 || height > 90) {
          newErrors.height = "Height must be between 50 and 90 inches";
        }
      }
    }

    // Weight validation
    if (!formData.weight) {
      newErrors.weight = "Weight is required";
    } else {
      const weight = Number(formData.weight);
      if (isMetric) {
        if (weight < 40 || weight > 200) {
          newErrors.weight = "Weight must be between 40kg and 200kg";
        }
      } else {
        if (weight < 88 || weight > 440) {
          newErrors.weight = "Weight must be between 88lbs and 440lbs";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateMacros = (): void => {
    if (!validateForm()) {
      return;
    }

    const weight = isMetric
      ? Number(formData.weight)
      : Number(formData.weight) * 0.453592;
    const height = isMetric
      ? Number(formData.height)
      : Number(formData.height) * 2.54;

    const bmr =
      formData.gender === "male"
        ? 10 * weight + 6.25 * height - 5 * Number(formData.age) + 5
        : 10 * weight + 6.25 * height - 5 * Number(formData.age) - 161;

    const activityMultipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      veryActive: 1.9,
    };

    const tdee =
      bmr *
      activityMultipliers[
        formData.activityLevel as keyof typeof activityMultipliers
      ];

    let calories = tdee;
    switch (formData.goal) {
      case "fatLoss":
        calories = tdee * 0.8;
        break;
      case "muscleGain":
        calories = tdee * 1.1;
        break;
    }

    const protein = weight * 2.2;
    const fat = (calories * 0.25) / 9;
    const carbs = (calories - protein * 4 - fat * 9) / 4;

    setResults({
      calories: Math.round(calories),
      protein: Math.round(protein),
      carbs: Math.round(carbs),
      fat: Math.round(fat),
    });

    trackCalculatorUse("macro_calculator", {
      unit_system: isMetric ? "metric" : "imperial",
      age: Number(formData.age),
      gender: formData.gender,
      height: Number(formData.height),
      weight: Number(formData.weight),
      goal: formData.goal,
      activity_level: formData.activityLevel,
    });
  };

  const chartData = results
    ? {
        labels: [
          `Protein (${Math.round(
            ((results.protein * 4) / results.calories) * 100
          )}%)`,
          `Carbs (${Math.round(
            ((results.carbs * 4) / results.calories) * 100
          )}%)`,
          `Fat (${Math.round(((results.fat * 9) / results.calories) * 100)}%)`,
        ],
        datasets: [
          {
            data: [
              ((results.protein * 4) / results.calories) * 100,
              ((results.carbs * 4) / results.calories) * 100,
              ((results.fat * 9) / results.calories) * 100,
            ],
            backgroundColor: ["#4F46E5", "#10B981", "#F59E0B"],
            borderWidth: 0,
          },
        ],
      }
    : null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    if (!/^\d*$/.test(value)) return; // Allow only numbers
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors({ ...errors, [name]: undefined });
    }
  };

  return (
    <div className="bg-white py-12 sm:py-18">
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <h1 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
          Macro Calculator
        </h1>
        <p className="mt-2 text-lg leading-8 text-gray-600">
          Calculate your daily macro needs based on your goals
        </p>

        <div className="mt-12 space-y-8">
          {/* Unit Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Units
            </label>
            <div className="inline-flex p-1 space-x-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => setIsMetric(true)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  isMetric
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                Metric
              </button>
              <button
                type="button"
                onClick={() => setIsMetric(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  !isMetric
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                Imperial
              </button>
            </div>
          </div>

          {/* Gender Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Gender
            </label>
            <div className="flex gap-4">
              {["male", "female"].map((gender) => (
                <button
                  key={gender}
                  type="button"
                  onClick={() => setFormData({ ...formData, gender })}
                  className={`flex-1 py-2 px-4 rounded-lg border ${
                    formData.gender === gender
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {gender.charAt(0).toUpperCase() + gender.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Age Input */}
          <div className="space-y-2">
            <label
              htmlFor="age"
              className="block text-sm font-medium text-gray-700"
            >
              Age
            </label>
            <input
              type="text"
              id="age"
              name="age"
              value={formData.age}
              onChange={handleInputChange}
              className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                errors.age ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.age && (
              <p className="mt-1 text-sm text-red-600">{errors.age}</p>
            )}
          </div>

          {/* Height Input */}
          <div className="space-y-2">
            <label
              htmlFor="height"
              className="block text-sm font-medium text-gray-700"
            >
              Height ({isMetric ? "cm" : "inches"})
            </label>
            <input
              type="text"
              id="height"
              name="height"
              value={formData.height}
              onChange={handleInputChange}
              className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                errors.height ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.height && (
              <p className="mt-1 text-sm text-red-600">{errors.height}</p>
            )}
          </div>

          {/* Weight Input */}
          <div className="space-y-2">
            <label
              htmlFor="weight"
              className="block text-sm font-medium text-gray-700"
            >
              Weight ({isMetric ? "kg" : "lbs"})
            </label>
            <input
              type="text"
              id="weight"
              name="weight"
              value={formData.weight}
              onChange={handleInputChange}
              className={`block w-full rounded-lg border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 ${
                errors.weight ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors.weight && (
              <p className="mt-1 text-sm text-red-600">{errors.weight}</p>
            )}
          </div>

          {/* Goal Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Goal
            </label>
            <div className="grid grid-cols-1 gap-2">
              {[
                { id: "fatLoss", label: "Fat Loss" },
                { id: "maintenance", label: "Maintenance" },
                { id: "muscleGain", label: "Muscle Gain" },
              ].map((goal) => (
                <button
                  key={goal.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, goal: goal.id })}
                  className={`py-2 px-4 rounded-lg border ${
                    formData.goal === goal.id
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {goal.label}
                </button>
              ))}
            </div>
          </div>

          {/* Activity Level Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Activity Level
            </label>
            <div className="grid grid-cols-1 gap-2">
              {[
                { id: "sedentary", label: "Sedentary (office job)" },
                { id: "light", label: "Light Exercise (1-2 days/week)" },
                {
                  id: "moderate",
                  label: "Moderate Exercise (3-5 days/week)",
                },
                { id: "active", label: "Heavy Exercise (6-7 days/week)" },
                { id: "veryActive", label: "Athlete (2x per day)" },
              ].map((level) => (
                <button
                  key={level.id}
                  type="button"
                  onClick={() =>
                    setFormData({ ...formData, activityLevel: level.id })
                  }
                  className={`py-2 px-4 rounded-lg border text-left ${
                    formData.activityLevel === level.id
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-900 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-center pt-6">
            <button
              onClick={calculateMacros}
              className="rounded-lg bg-indigo-600 px-8 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              Calculate
            </button>
          </div>
        </div>

        {results && (
          <div className="mt-12 space-y-12">
            {/* Daily Macros */}
            <div className="rounded-lg bg-gray-50 p-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Your Daily Macros
              </h3>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-lg bg-white p-4 text-center shadow">
                  <div className="text-sm text-gray-500">Calories</div>
                  <div className="mt-1 text-xl font-semibold text-gray-900">
                    {results.calories}
                  </div>
                </div>
                <div className="rounded-lg bg-white p-4 text-center shadow">
                  <div className="text-sm text-gray-500">Protein</div>
                  <div className="mt-1 text-xl font-semibold text-gray-900">
                    {results.protein}g
                  </div>
                </div>
                <div className="rounded-lg bg-white p-4 text-center shadow">
                  <div className="text-sm text-gray-500">Carbs</div>
                  <div className="mt-1 text-xl font-semibold text-gray-900">
                    {results.carbs}g
                  </div>
                </div>
                <div className="rounded-lg bg-white p-4 text-center shadow">
                  <div className="text-sm text-gray-500">Fat</div>
                  <div className="mt-1 text-xl font-semibold text-gray-900">
                    {results.fat}g
                  </div>
                </div>
              </div>

              {/* Macro Distribution Chart */}
              <div className="mt-8 flex justify-center">
                <div className="w-64">
                  {chartData && (
                    <div className="relative">
                      <MacroDoughnutChart
                        data={chartData}
                        options={{
                          plugins: {
                            legend: {
                              position: "bottom",
                            },
                            tooltip: {
                              enabled: true,
                              callbacks: {
                                label: function (context) {
                                  return `${context.label}`;
                                },
                              },
                            },
                          },
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Weekly Overview */}
            <div className="rounded-lg bg-gray-50 p-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Weekly Overview
              </h3>
              <div className="mt-4 space-y-4">
                {Array.from({ length: 7 }, (_, i) => (
                  <div key={i} className="rounded-lg bg-white p-4 shadow">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-gray-900">
                        {
                          [
                            "Sunday",
                            "Monday",
                            "Tuesday",
                            "Wednesday",
                            "Thursday",
                            "Friday",
                            "Saturday",
                          ][i]
                        }
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div className="text-center">
                          <div className="text-gray-500">Cal</div>
                          <div className="mt-1 text-xl font-semibold text-gray-900">
                            {results.calories}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500">P</div>
                          <div className="font-semibold">
                            {results.protein}g
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500">C</div>
                          <div className="font-semibold">{results.carbs}g</div>
                        </div>
                        <div className="text-center">
                          <div className="text-gray-500">F</div>
                          <div className="font-semibold">{results.fat}g</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
